/**
 * Persistent, self-evicting cache for the static suburb GeoJSON (~2.9MB).
 *
 * The polygon file is regenerated at most once a month, so re-downloading it
 * every session is waste. The Cache API stores the raw bytes on disk and
 * persists across sessions. Freshness is verified with an HTTP conditional
 * request (If-None-Match / If-Modified-Since): a 304 keeps the cache with ~0
 * bytes, a 200 overwrites it (auto-evict) when the file actually changes.
 *
 * Falls back to a plain fetch() when CacheStorage is unavailable.
 */

const CACHE_NAME = 'v9-suburb-geojson-v1';
const CACHE_PREFIX = 'v9-suburb-geojson-';
const STAMP_HEADER = 'x-v9-cached-at';
const ETAG_HEADER = 'x-v9-etag';
const LASTMOD_HEADER = 'x-v9-last-mod';

let revalidatedThisSession = false;
let revalidationInFlight: Promise<void> | null = null;

function cacheAvailable(): boolean {
  return typeof caches !== 'undefined';
}

async function storeGeojson(
  url: string,
  blob: Blob,
  { etag = '', lastModified = '' }: { etag?: string; lastModified?: string } = {},
): Promise<void> {
  if (!cacheAvailable()) return;
  try {
    const cache = await caches.open(CACHE_NAME);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      [STAMP_HEADER]: String(Date.now()),
    };
    if (etag) headers[ETAG_HEADER] = etag;
    if (lastModified) headers[LASTMOD_HEADER] = lastModified;
    await cache.put(url, new Response(blob, { headers }));
    const keys = await caches.keys();
    await Promise.all(
      keys.filter((k) => k.startsWith(CACHE_PREFIX) && k !== CACHE_NAME).map((k) => caches.delete(k)),
    );
  } catch {
    /* non-fatal — next load re-fetches */
  }
}

/** Conditional revalidation — runs once per session; leaves the cache holding the current file. */
export function revalidateGeojson(url: string): Promise<void> {
  if (revalidatedThisSession) return Promise.resolve();
  if (revalidationInFlight) return revalidationInFlight;
  if (!cacheAvailable()) {
    revalidatedThisSession = true;
    return Promise.resolve();
  }

  revalidationInFlight = (async () => {
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(url);
    const etag = cached?.headers.get(ETAG_HEADER);
    const lastMod = cached?.headers.get(LASTMOD_HEADER);

    const headers: Record<string, string> = {};
    if (etag) headers['If-None-Match'] = etag;
    if (lastMod) headers['If-Modified-Since'] = lastMod;

    let res: Response;
    try {
      res = await fetch(url, Object.keys(headers).length ? { headers } : undefined);
    } catch {
      return; // offline — keep cache
    }

    if (res.status === 304 && cached) return; // unchanged
    if (!res.ok) return;

    const blob = await res.blob();
    await storeGeojson(url, blob, {
      etag: res.headers.get('etag') || '',
      lastModified: res.headers.get('last-modified') || '',
    });
  })().finally(() => {
    revalidatedThisSession = true;
    revalidationInFlight = null;
  });

  return revalidationInFlight;
}

/** Cache-first fetch — revalidates once, then serves the cached Response. */
export async function fetchGeojsonCached(url: string): Promise<Response> {
  if (!cacheAvailable()) return fetch(url);
  try {
    await revalidateGeojson(url);
    const cache = await caches.open(CACHE_NAME);
    const hit = await cache.match(url);
    if (hit) return hit;
  } catch {
    /* fall through */
  }
  return fetch(url);
}
